import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect, useId, useRef, useState } from 'react'
import type { FieldValues, SubmitHandler, UseFormProps, UseFormReturn } from 'react-hook-form'
import { FormProvider, useForm } from 'react-hook-form'
import type { z } from 'zod'

import { getLocaleParts } from '@constants/index'
import { IntlFormatter } from '@formatting/IntlFormatter'
import { zodResolver } from '@hookform/resolvers/zod'
import { getDefaultErrorMap } from '@validation/errorMap'

const TEXT_FIELDS =
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([disabled])'

const picksAnOption = (input: HTMLInputElement) =>
  input.hasAttribute('aria-autocomplete') || input.id.startsWith('filter-input-')

const focusNextFieldOnEnter = (event: KeyboardEvent) => {
  const input = event.target
  const formElement = event.currentTarget
  if (
    event.key !== 'Enter' ||
    !(input instanceof HTMLInputElement) ||
    !(formElement instanceof HTMLFormElement) ||
    picksAnOption(input)
  ) {
    return
  }
  const fields = Array.from(formElement.querySelectorAll<HTMLInputElement>(TEXT_FIELDS)).filter(
    (field) => field.offsetParent !== null
  )
  const next = fields[fields.indexOf(input) + 1]
  if (next) {
    event.preventDefault()
    next.focus()
  }
}

export type UseZodForm<TInput extends FieldValues> = UseFormReturn<TInput> & {
  /**
   * A unique ID for this form.
   */
  id: string
}

export const useZodForm = <TSchema extends z.ZodType>(
  props: Omit<UseFormProps<TSchema['_input']>, 'resolver'> & {
    schema: TSchema
  }
) => {
  const { locale } = useRouter()
  const [language, market] = getLocaleParts(locale)
  const formatter = new IntlFormatter({
    getCurrentLanguage: () => language,
    getCurrentMarket: () => market,
  })
  const { i18n } = useTranslation()
  const form = useForm<TSchema['_input']>({
    mode: 'onChange',
    ...props,
    resolver: zodResolver(
      props.schema,
      {
        errorMap: getDefaultErrorMap(i18n, formatter),
      },
      {
        raw: true,
      }
    ),
  }) as UseZodForm<TSchema['_input']>

  form.id = useId()

  return form
}

export const ZodForm = <TInput extends FieldValues>(
  props: Omit<React.ComponentProps<'form'>, 'onSubmit' | 'id'> & {
    handleSubmit: SubmitHandler<TInput>
    form: UseZodForm<TInput>
    autoSubmit: boolean
    stepName: string
    focusNextFieldOnEnter?: boolean
  }
) => {
  const [submittedStep, setSubmittedStep] = useState<string | null>(null)
  const {
    handleSubmit,
    form,
    autoSubmit,
    stepName,
    focusNextFieldOnEnter: moveFocusOnEnter,
    ...passThrough
  }: typeof props = props
  const { formState } = form

  const submitRef = useRef(handleSubmit)
  submitRef.current = handleSubmit

  const formRef = useRef<HTMLFormElement>(null)
  useEffect(() => {
    const formElement = formRef.current
    if (!moveFocusOnEnter) {
      return undefined
    }
    formElement?.addEventListener('keydown', focusNextFieldOnEnter)
    return () => formElement?.removeEventListener('keydown', focusNextFieldOnEnter)
  }, [moveFocusOnEnter])

  useEffect(() => {
    if (!autoSubmit || submittedStep === stepName || !formState.isDirty || !formState.isValid) {
      return
    }
    setSubmittedStep(stepName)
    void submitRef.current(form.getValues())
  }, [autoSubmit, form, formState.isDirty, formState.isValid, stepName, submittedStep])

  return (
    <FormProvider {...form}>
      <form
        {...passThrough}
        id={form.id}
        className="flex flex-1 flex-col"
        ref={formRef}
        noValidate
        onSubmit={(event) => {
          return form.handleSubmit(async (values) => {
            try {
              await handleSubmit(values)
            } catch (error) {
              form.setError('root.server', {
                message: (error as Error)?.message ?? 'Unknown error',
                type: 'server',
              })
            }
          })(event)
        }}
      />
    </FormProvider>
  )
}
